import { useState } from 'react'

import {
  Autocomplete,
  Chip,
  AutocompleteProps as UIAutocompleteProps,
  useDebouncedValue,
  UserInfo,
} from '@collaborate-health/uikit'
import { UserSearchInfoResponse } from '@tabeeb/contracts/main-api/swagger-main-api'
import { useUsers } from '@tabeeb/modules/../users/hooks'

export interface UserSearchProps<TFreeSolo extends boolean = false, TMultiple extends boolean = false>
  extends Omit<UIAutocompleteProps<UserSearchInfoResponse, TMultiple, undefined, TFreeSolo>, 'options' | 'loading'> {
  minSearchLength?: number
}

const UserSearch = <TFreeSolo extends boolean = false, TMultiple extends boolean = false>({
  freeSolo = false as TFreeSolo,
  multiple = false as TMultiple,
  label = 'Enter name or email',
  placeholder = 'Enter name or email...',
  error,
  getOptionLabel,
  renderOption,
  renderTags,
  minSearchLength = 3,
  ...rest
}: UserSearchProps<TFreeSolo, TMultiple>) => {
  const [searchUserEmail, setSearchUserEmail] = useState('')
  const debouncedSearchUserEmail = useDebouncedValue(searchUserEmail, 500)

  const { loading, users } = useUsers({
    search: debouncedSearchUserEmail,
    minSearchLength,
  })

  return (
    <Autocomplete
      freeSolo={freeSolo}
      multiple={multiple}
      {...rest}
      loading={loading}
      options={users}
      renderTags={
        renderTags ??
        ((options, getItemProps) =>
          options.map((user, index) => (
            <Chip
              variant='two-tone'
              color='primary'
              size='small'
              maxWidth={260}
              label={user.Email}
              title={user.UserName!}
              {...getItemProps({ index })}
            />
          )))
      }
      renderOption={
        renderOption ??
        ((props, option) => (
          <li {...props}>
            <UserInfo item={option as any} />
          </li>
        ))
      }
      getOptionLabel={
        getOptionLabel ??
        // @ts-ignore MUI fails to infer the correct type here
        ((option) => option?.Email)
      }
      error={error}
      label={label}
      placeholder={placeholder ?? 'Enter an email...'}
      onInputChange={(e, value) => {
        setSearchUserEmail(value)
      }}
    />
  )
}

export default UserSearch

